// hooks/useWebsiteCounter.js
import { useState, useEffect, useRef } from 'react';
import { database, ref, set, increment, get } from '../firebase.config';

const MONTH_NAMES = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec"
];

export const useWebsiteCounter = () => {
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [years, setYears] = useState([]);
  
  // Use a ref to track if we've already incremented
  const hasIncremented = useRef(false);

  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear().toString();
    const monthIndex = now.getMonth();
    const monthName = MONTH_NAMES[monthIndex];
    return { year, monthName };
  };

  // Check if already counted in this session
  const isFirstVisit = () => {
    return !sessionStorage.getItem('websiteVisitCounted');
  };

  // Increment count - only run once
  const incrementCount = async () => {
    // Prevent multiple increments
    if (hasIncremented.current || !isFirstVisit()) {
      console.log("Already counted in this session or already incremented");
      return;
    }

    hasIncremented.current = true;
    const { year, monthName } = getCurrentDate();

    try {
      const countRef = ref(database, `websiteCount/${year}/${monthName}`);
      await set(countRef, increment(1));
      sessionStorage.setItem('websiteVisitCounted', 'true');
      console.log(`Count incremented for ${monthName} ${year}`);
      
      // Update local total count
      const total = await getTotalCount();
      setTotalCount(total + 1);
    } catch (error) {
      console.error("Error incrementing count:", error);
    }
  };

  // Get total count
  const getTotalCount = async () => {
    try {
      const snapshot = await get(ref(database, 'websiteCount'));
      if (!snapshot.exists()) return 0;
      
      const allCounts = snapshot.val();
      let total = 0;

      Object.values(allCounts).forEach(yearData => {
        Object.values(yearData).forEach(monthCount => {
          total += monthCount;
        });
      });

      return total;
    } catch (error) {
      console.error("Error getting total count:", error);
      return 0;
    }
  };

  // Fetch and store total count
  const fetchTotalCount = async () => {
    try {
      const total = await getTotalCount();
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching total count:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Extract all available years from the data
  const extractYears = async () => {
    try {
      const snapshot = await get(ref(database, 'websiteCount'));
      if (!snapshot.exists()) {
        setYears([]);
        return [];
      }
      
      const allCounts = snapshot.val();
      const yearList = Object.keys(allCounts).sort((a, b) => b - a);
      setYears(yearList);
      return yearList;
    } catch (error) {
      console.error("Error extracting years:", error);
      setYears([]);
      return [];
    }
  };

  // Get monthly counts for a specific year
  const getMonthlyCountsForYear = async (year) => {
    try {
      const monthlyCounts = new Array(12).fill(0);
      const snapshot = await get(ref(database, `websiteCount/${year}`));

      if (snapshot.exists()) {
        const yearData = snapshot.val();
        MONTH_NAMES.forEach((monthName, index) => {
          if (yearData[monthName]) {
            monthlyCounts[index] = yearData[monthName];
          }
        });
      }

      return monthlyCounts;
    } catch (error) {
      console.error(`Error getting monthly counts for ${year}:`, error);
      return new Array(12).fill(0);
    }
  };

  // Initialize only once
  useEffect(() => {
    const initializeCounter = async () => {
      // First increment if needed
      await incrementCount();
      
      // Then fetch all data
      await fetchTotalCount();
      await extractYears();
    };

    initializeCounter();
    
    // Cleanup function to reset the ref if needed
    return () => {
      // hasIncremented.current = false; // Uncomment if you want to allow increment on remount
    };
  }, []);

  return {
    totalCount,
    isLoading,
    years,
    incrementCount,
    getTotalCount,
    getMonthlyCountsForYear,
    extractYears,
    fetchTotalCount
  };
};